import React, { useCallback, useMemo, useState } from 'react';

import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table';
import { useForm } from 'react-hook-form';

import { FilterSection } from '../../../components/FilterSection';
import { ControlledDateTimeField } from '../../../components/FormControlsV2/ControlledDateTimeField';
import { ControlledSelectField } from '../../../components/FormControlsV2/ControlledSelectField';
import { ControlledTextField } from '../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../components/FormControlsV2/FormProvider';
import { TableGridLocal } from '../../../components/TableGridLocal';
import { InformationDialog } from '../../../components/UIKit/InformationDialog';
import { StrategyDictionary$ } from '../../../effector/strategyDictionary';
import { useAdministrationAuditLogs } from '../../../hooks/useAdministrationAuditLogs';
import { AuditLog, AuditLogRow } from '../../../types/auditLog';
import { Criteria, Filter, PageParams, SortType } from '../../../types/pagination';
import { parseApiError } from '../../../utils/apiErrors';

type FormFields = {
  login: string;
  strategyId: string;
  date: string | Date | null;
};

const EMPTY_FILTERS: FormFields = {
  login: '',
  strategyId: '',
  date: '',
};

const DEFAULT_TABLE_PARAMS: PageParams = {
  pageNumber: 0,
  pageSize: 25,
  sortFieldName: 'date',
  sortDirection: SortType.Desk,
};

const DEFAULT_COLUMN_OPTIONS = {
  enableColumnFilter: false,
  enableColumnOrdering: false,
  enableColumnFilterModes: false,
  enableColumnActions: false,
};

const PREVIEW_MAX_LENGTH = 180;

const mapAuditLogRow = (item: AuditLog, index: number): AuditLogRow => ({
  id: `${item.date}-${item.userLogin}-${item.action}-${index}`,
  date: item.date,
  ipAddress: item.ipAddress,
  login: item.userLogin,
  action: item.action,
  information: item.infoPreview,
});

const getPreviewText = (value: string, maxLength = PREVIEW_MAX_LENGTH) => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}...`;
};

type AuditLogInfoCellProps = {
  information: string;
  onOpen: (value: string) => void;
};

const AuditLogInfoCell: React.FC<AuditLogInfoCellProps> = ({ information, onOpen }) => {
  const isLongText = information.length > PREVIEW_MAX_LENGTH;

  return (
    <Box>
      <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{getPreviewText(information)}</Box>
      {isLongText && (
        <Button sx={{ mt: 1, px: 0, minWidth: 'auto' }} onClick={() => onOpen(information)}>
          Подробнее
        </Button>
      )}
    </Box>
  );
};

export const AdministrationAuditLog: React.FC = () => {
  const strategies = useUnit(StrategyDictionary$);
  const { data, mutate: getAuditLogs, isPending: isLoading } = useAdministrationAuditLogs();
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedInformation, setSelectedInformation] = useState<string | null>(null);
  const [submittedFilters, setSubmittedFilters] = useState<FormFields>(EMPTY_FILTERS);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: EMPTY_FILTERS,
  });

  const strategyOptions = useMemo(
    () => strategies?.map((strategy) => ({ value: strategy.id, label: strategy.name })) || [],
    [strategies],
  );

  const buildFilters = useCallback((values: FormFields) => {
    const filters: Filter[] = [];

    if (values.login.trim()) {
      filters.push({
        fieldName: 'login',
        filterCriteria: Criteria.Contains,
        matches: [values.login.trim()],
      });
    }

    if (values.strategyId) {
      filters.push({
        fieldName: 'strategyId',
        filterCriteria: Criteria.Equals,
        matches: [values.strategyId],
      });
    }

    if (values.date) {
      filters.push({
        fieldName: 'date',
        filterCriteria: Criteria.Contains,
        matches: [dayjs(values.date).format('YYYY-MM-DD')],
      });
    }

    return filters;
  }, []);

  const fetchAuditLogs = useCallback(
    (params: PageParams, filters: FormFields) => {
      setApiError(null);

      getAuditLogs(
        {
          ...params,
          filters: buildFilters(filters),
        },
        {
          onError: (error) => {
            setApiError(parseApiError(error, 'Не удалось загрузить журнал событий.'));
          },
        },
      );
    },
    [buildFilters, getAuditLogs],
  );

  const onSubmit = handleSubmit((values) => {
    setSubmittedFilters(values);
    fetchAuditLogs(DEFAULT_TABLE_PARAMS, values);
  });

  const handleReset = useCallback(() => {
    reset(EMPTY_FILTERS);
    setSubmittedFilters(EMPTY_FILTERS);
    fetchAuditLogs(DEFAULT_TABLE_PARAMS, EMPTY_FILTERS);
  }, [fetchAuditLogs, reset]);

  const columns = useMemo<MRT_ColumnDef<AuditLogRow>[]>(
    () => [
      {
        accessorKey: 'date',
        header: 'Дата',
        ...DEFAULT_COLUMN_OPTIONS,
        Cell: ({ cell }) => {
          const value = cell.row.original.date;
          return <span>{value ? dayjs(value).format('DD.MM.YYYY HH:mm') : ''}</span>;
        },
      },
      {
        accessorKey: 'ipAddress',
        header: 'IP адрес',
        ...DEFAULT_COLUMN_OPTIONS,
      },
      {
        accessorKey: 'login',
        header: 'Пользователь',
        ...DEFAULT_COLUMN_OPTIONS,
      },
      {
        accessorKey: 'action',
        header: 'Действие',
        ...DEFAULT_COLUMN_OPTIONS,
      },
      {
        accessorKey: 'information',
        header: 'Информация',
        ...DEFAULT_COLUMN_OPTIONS,
        enableSorting: false,
        Cell: ({ cell }) => (
          <AuditLogInfoCell information={cell.row.original.information} onOpen={setSelectedInformation} />
        ),
      },
    ],
    [],
  );

  const columnSorts = useMemo<MRT_SortingState>(
    () => [
      {
        desc: true,
        id: 'date',
      },
    ],
    [],
  );

  const rows = useMemo(() => {
    return (data?.result?.pageItems || []).map(mapAuditLogRow);
  }, [data]);

  const handleFetchData = useCallback(
    (params: PageParams) => {
      fetchAuditLogs(params, submittedFilters);
    },
    [fetchAuditLogs, submittedFilters],
  );

  return (
    <>
      <FilterSection>
        <form onSubmit={onSubmit}>
          <FormProvider control={control} errors={errors}>
            <Stack direction="row" gap={2}>
              <ControlledTextField name="login" label="Пользователь" />
              <ControlledSelectField name="strategyId" label="Стратегия" options={strategyOptions} />
            </Stack>

            <Stack direction="row" gap={2}>
              <ControlledDateTimeField name="date" label="Дата" format="DD.MM.YYYY" />
            </Stack>
          </FormProvider>

          <Stack direction="row" gap={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              sx={{ mt: 2 }}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              Найти
            </Button>
            <Button variant="outlined" color="primary" size="large" fullWidth sx={{ mt: 2 }} onClick={handleReset}>
              Сбросить
            </Button>
          </Stack>

          {apiError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setApiError(null)}>
              {apiError}
            </Alert>
          )}
        </form>
      </FilterSection>

      <div style={{ marginTop: '30px' }}>
        <TableGridLocal
          name="administration-audit-log"
          serverMode
          isLoading={isLoading}
          columns={columns}
          data={rows}
          pageSize={25}
          columnSorts={columnSorts}
          totalRowCount={data?.result?.total || 0}
          onFetchData={handleFetchData}
        />
      </div>

      {selectedInformation && (
        <InformationDialog
          title="Информация о событии"
          description={
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selectedInformation}</pre>
          }
          onClose={() => setSelectedInformation(null)}
        />
      )}
    </>
  );
};
